import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useFirestore } from '../../firebase/FirebaseProvider'
import { PlayerType } from '../GameApi/BaseGameApi'
import type { InProgressGameApi } from '../GameApi/InProgressGameApi'
import {
  type ConnectionStatus,
  deriveConnectionStatus,
  estimateServerNow,
  isOpponentOnline,
} from '../services/connectionStatus'

const HEARTBEAT_MS = 8_000
const TICK_MS = 1_000

export interface ConnectionState {
  status: ConnectionStatus
  /** null while connecting (no presence data yet). */
  opponentOnline: boolean | null
}

function lastSeenMillis(snapData: unknown): number | null {
  const ts = (
    snapData as { lastSeen?: { toMillis?: () => number } } | undefined
  )?.lastSeen
  return ts && typeof ts.toMillis === 'function' ? ts.toMillis() : null
}

export function useConnectionStatus(
  gameApi: InProgressGameApi,
): ConnectionState {
  const firestore = useFirestore()
  const gameId = gameApi.gameId
  const myUid = gameApi.getUserId()
  const opponentUid = gameApi.getOpponentUserId()
  const participant = gameApi.isParticipant()
  const isOpponentTurn = gameApi.getTurn() === PlayerType.OPPONENT

  const ownServerRef = useRef<{
    lastSeenMs: number
    recordedAtLocal: number
  } | null>(null)

  const [now, setNow] = useState(() => Date.now())
  const [pendingPingSince, setPendingPingSince] = useState<number | null>(null)
  const [opponentLastSeenMs, setOpponentLastSeenMs] = useState<number | null>(
    null,
  )
  // Presence access can fail definitively (e.g. permission-denied if the rules
  // aren't deployed, or a validation error). That is NOT network slowness, so we
  // disable the indicator rather than show a misleading "connection slow" banner.
  const [presenceUnavailable, setPresenceUnavailable] = useState(false)
  const unavailableRef = useRef(false)

  const markPresenceUnavailable = useCallback(() => {
    unavailableRef.current = true
    setPresenceUnavailable(true)
    setPendingPingSince(null)
  }, [])

  // 1s ticker so an unanswered heartbeat is noticed even without new snapshots.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [])

  // Heartbeat writes + own-presence echo. The round trip (write -> backend ->
  // server-confirmed snapshot back to us) is our local-connection health probe,
  // and the echoed server timestamp is our skew-free "server now" reference.
  useEffect(() => {
    if (!participant) {
      return
    }
    const myRef = doc(firestore, 'games', gameId, 'presence', myUid)

    function beat() {
      if (unavailableRef.current || document.visibilityState !== 'visible') {
        return
      }
      // Mark a round trip outstanding (keep the oldest unanswered one).
      setPendingPingSince((prev) => prev ?? Date.now())
      void setDoc(myRef, { lastSeen: serverTimestamp() }).catch(() => {
        // A REJECTED write is a definitive backend response (permission or
        // validation), not network slowness — slowness leaves the write pending.
        // Disable presence instead of surfacing a false "connection slow".
        markPresenceUnavailable()
      })
    }

    beat()
    const id = setInterval(beat, HEARTBEAT_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        beat()
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    const unsubOwn = onSnapshot(
      myRef,
      { includeMetadataChanges: true },
      (snap) => {
        // Skip the optimistic local echo; only the backend-confirmed snapshot
        // proves the round trip completed.
        if (snap.metadata.fromCache) {
          return
        }
        const ms = lastSeenMillis(snap.data())
        if (ms !== null) {
          ownServerRef.current = { lastSeenMs: ms, recordedAtLocal: Date.now() }
          setPendingPingSince(null)
        }
      },
      markPresenceUnavailable,
    )

    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
      unsubOwn()
    }
    // firestore is a stable singleton (useMemo([]) in FirebaseProvider) —
    // intentionally omitted from deps so the heartbeat doesn't re-subscribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, myUid, participant, markPresenceUnavailable])

  // Opponent presence read.
  useEffect(() => {
    if (!opponentUid) {
      return
    }
    const oppRef = doc(firestore, 'games', gameId, 'presence', opponentUid)
    const unsub = onSnapshot(
      oppRef,
      (snap) => {
        setOpponentLastSeenMs(lastSeenMillis(snap.data()))
      },
      markPresenceUnavailable,
    )
    return unsub
    // firestore is a stable singleton — intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, opponentUid, markPresenceUnavailable])

  // Presence is unusable (e.g. rules not deployed) — degrade silently to "ok"
  // with an unknown opponent rather than a misleading "connection slow" banner.
  if (presenceUnavailable) {
    return { status: 'ok', opponentOnline: null }
  }

  const serverNowMs = estimateServerNow(
    ownServerRef.current?.lastSeenMs ?? null,
    ownServerRef.current?.recordedAtLocal ?? null,
    now,
  )

  const status = deriveConnectionStatus({
    now,
    pendingPingSince,
    serverNowMs,
    opponentLastSeenMs,
    isOpponentTurn,
  })

  return {
    status,
    opponentOnline: isOpponentOnline(serverNowMs, opponentLastSeenMs),
  }
}
